import { FC, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, useBreakpoint } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { AlertCircleLine, CheckCircleLine, Pig } from '@factorialco/f0-react/icons/app'
import {
  GetUpsellDiscountsDocument,
  MarketplaceModulesDataCollectionDocument,
  ModulesDatoModule,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { locale } from 'modules/marketplace_modules/components/utils/locales'

const RevampList: FC = () => {
  const queryParams = new URLSearchParams(location.search)
  const upsellOrigin = queryParams.get('upsell_origin')
  const { navigation } = useNavigation()
  const { breakpoint } = useBreakpoint()
  const isLargeScreen = breakpoint === 'xxxl'

  const { moduleAvailabilities } = useModuleAvailabilities()
  const session = useCurrentSession()
  const currentFiltersRef = useRef<{
    status?: Array<boolean>
  }>({})

  const datoFormatLocale = locale[session.access.locale]
  const countryCodeForCompany = countryCode(session.company.legalEntities[0]?.country || '')

  const isModuleAvailable = (chargebeeModuleName: string) => {
    const availableModule = moduleAvailabilities.filter((obj) => {
      const modulesArray = chargebeeModuleName?.split(',')
      return modulesArray?.includes(obj.id)
    })[0]

    return availableModule?.available ?? false
  }

  const { search: searchParams } = useLocation()

  const preview = new URLSearchParams(searchParams).get('preview') === 'true'

  const applyClientSideFilters = (items: ModulesDatoModule[]) => {
    let filteredItems = [...items]

    if (currentFiltersRef.current.status && currentFiltersRef.current.status.length > 0) {
      filteredItems = filteredItems.filter((item) => {
        const isInstalled = isModuleAvailable(item.chargebeeModuleName || '')
        const statusFilter = currentFiltersRef.current.status || []

        if (statusFilter.includes(true) && statusFilter.includes(false)) {
          return true
        }

        if (statusFilter.includes(true) && !statusFilter.includes(false)) {
          return isInstalled
        }

        if (statusFilter.includes(false) && !statusFilter.includes(true)) {
          return !isInstalled
        }

        return true
      })
    }

    return filteredItems
  }

  const sortModulesByInstallationStatus = (items: ModulesDatoModule[]) => {
    return items.sort((a, b) => {
      const aIsInstalled = isModuleAvailable(a.chargebeeModuleName || '')
      const bIsInstalled = isModuleAvailable(b.chargebeeModuleName || '')

      if (aIsInstalled === bIsInstalled) return 0

      return aIsInstalled ? 1 : -1
    })
  }

  const { data: discountsData } = useQuery(GetUpsellDiscountsDocument)

  const source = useGraphqlDataCollectionSource(
    {
      document: MarketplaceModulesDataCollectionDocument,
      mapVariables: ({ search, filters }) => {
        currentFiltersRef.current = {
          status: filters.status,
        }

        return {
          search,
          locale: datoFormatLocale,
          countryCode: countryCodeForCompany,
          companyId: session.company.id,
          preview,
        }
      },
      search: {
        enabled: true,
      },
      filters: {
        status: {
          label: i18n.t('marketplace.revamp.filters.status'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('marketplace.revamp.in_your_plan'), value: true },
              {
                label: i18n.t('marketplace.revamp.not_in_your_plan'),
                value: false,
              },
            ],
          },
        },
      },
      presets: [
        {
          label: i18n.t('marketplace.revamp.in_your_plan'),
          filter: {
            status: [true],
          },
        },
        {
          label: i18n.t('marketplace.revamp.not_in_your_plan'),
          filter: {
            status: [false],
          },
        },
      ],

      mapData: (data) => {
        const baseItems = data.modules.datoModulesConnection.nodes
        const filteredItems = applyClientSideFilters(baseItems)

        return sortModulesByInstallationStatus(filteredItems)
      },
      mapPagination: (data) => {
        const baseItems = data.modules.datoModulesConnection.nodes
        const filteredItems = applyClientSideFilters(baseItems)

        const sortedItems = sortModulesByInstallationStatus(filteredItems)

        return {
          total: sortedItems.length,
        }
      },
      paginationType: 'infinite-scroll',
      itemUrl: (item: ModulesDatoModule) =>
        `${navigation.discover.marketplaceModules.item.fullPath({
          id: item.slug,
        })}?upsell_origin=${upsellOrigin}`,
    },
    [moduleAvailabilities]
  )
  // Container styles with responsive logic
  const containerStyle = {
    ...(isLargeScreen && { width: '1920px', margin: '0 auto' }),
  }

  const hasDiscount = (item: ModulesDatoModule) => {
    const data = discountsData?.upsell?.upsellDiscountsConnection?.nodes
    const discount = data?.find(
      (discount_item) => discount_item.module === item.chargebeeModuleName
    )
    return discount
  }

  return (
    <Box style={containerStyle}>
      <OneDataCollection
        source={source}
        fullHeight
        visualizations={[
          {
            type: 'card',
            options: {
              title: (item: ModulesDatoModule) => item.translatedName || '',
              image: (item: ModulesDatoModule) => item.thumbnailImage?.url || '',
              description: (item: ModulesDatoModule) => item.shortDescription || '',
              cardProperties: [
                {
                  label: i18n.t('marketplace.revamp.filters.status'),
                  icon: CheckCircleLine,
                  render: (item: ModulesDatoModule) =>
                    isModuleAvailable(item.chargebeeModuleName || '')
                      ? {
                          type: 'status',
                          value: {
                            status: 'positive',

                            label: i18n.t('marketplace.revamp.in_your_plan'),
                          },
                        }
                      : undefined,
                },
                {
                  label: i18n.t('marketplace.revamp.filters.status'),
                  icon: AlertCircleLine,
                  render: (item: ModulesDatoModule) =>
                    !isModuleAvailable(item.chargebeeModuleName || '')
                      ? {
                          type: 'status',
                          value: {
                            status: 'neutral',

                            label: i18n.t('marketplace.revamp.not_in_your_plan'),
                          },
                        }
                      : undefined,
                },
                {
                  label: 'Discount',
                  icon: Pig,
                  render: (item: ModulesDatoModule) =>
                    hasDiscount(item) && !isModuleAvailable(item.chargebeeModuleName || '')
                      ? {
                          type: 'status',
                          value: {
                            status: 'warning',
                            label: i18n.t('marketplace.revamp.list.discount', {
                              percentage: Math.floor(hasDiscount(item)?.percentage || 0),
                            }),
                          },
                        }
                      : undefined,
                },
              ],
            },
          },
        ]}
      />
    </Box>
  )
}

export default RevampList
